import { useEffect } from "react"

/**
 * Prevents the page behind overlays from scrolling. Ref-counted so multiple
 * modals/drawers stack correctly: the lock is released only when the last
 * `locked: true` consumer unmounts or sets `locked` to false.
 *
 * Uses `overflow: hidden` on `html` / `body` only — not `position: fixed` on
 * `body`, which breaks window scrolling in this app’s layout and was leaving
 * list/pagination areas non-scrollable after overlays closed.
 */
let scrollLockCount = 0
let lockSnapshot: {
  htmlOverflow: string
  bodyOverflow: string
  bodyPaddingRight: string
} | null = null

function takeLock() {
  scrollLockCount += 1
  if (scrollLockCount > 1) return

  const html = document.documentElement
  const body = document.body
  const scrollbarWidth = Math.max(0, window.innerWidth - html.clientWidth)
  lockSnapshot = {
    htmlOverflow: html.style.overflow,
    bodyOverflow: body.style.overflow,
    bodyPaddingRight: body.style.paddingRight,
  }
  html.style.overflow = "hidden"
  body.style.overflow = "hidden"
  /** Keeps centered modals from jumping when the vertical scrollbar disappears. */
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`
  }
}

function releaseLock() {
  if (scrollLockCount <= 0) return
  scrollLockCount -= 1
  if (scrollLockCount > 0) return

  const html = document.documentElement
  const body = document.body
  if (lockSnapshot) {
    html.style.overflow = lockSnapshot.htmlOverflow
    body.style.overflow = lockSnapshot.bodyOverflow
    body.style.paddingRight = lockSnapshot.bodyPaddingRight
  } else {
    html.style.removeProperty("overflow")
    body.style.removeProperty("overflow")
    body.style.removeProperty("padding-right")
  }
  lockSnapshot = null
}

export function useBodyScrollLock(locked: boolean) {
  useEffect(() => {
    if (!locked) return
    takeLock()
    return () => {
      releaseLock()
    }
  }, [locked])
}
